import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  IconButton,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Edit,
  Delete,
  SmartToy,
  CheckCircle,
  HourglassEmpty,
  Cancel,
  PlayCircleOutline,
} from '@mui/icons-material';
import {SeverityBadge} from './SeverityBadge';

type DefectSeverity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
type DefectStatus =
  | 'IDENTIFIED'
  | 'IN_REVIEW'
  | 'ACCEPTED'
  | 'RESOLVED'
  | 'REJECTED';

interface DefectCardProps {
  defect: {
    id: string;
    title: string;
    description?: string | null;
    severity: DefectSeverity;
    defectStatus: DefectStatus;
    roomType?: string | null;
    defectCategory?: string | null;
    sourceClipIndex?: number | null;
    sourceFramePosition?: string | null;
    aiDetected: boolean;
    aiConfidence?: number | null;
    evidenceImageURI?: string | null;
    audioStartSeconds?: number | null;
    audioEndSeconds?: number | null;
  };
  onEdit?: () => void;
  onDelete?: () => void;
  onPlayAudio?: () => void;
}

const statusConfig: Record<
  DefectStatus,
  {icon: React.ReactNode; color: string}
> = {
  IDENTIFIED: {
    icon: <HourglassEmpty fontSize="small" />,
    color: 'warning.main',
  },
  IN_REVIEW: {icon: <HourglassEmpty fontSize="small" />, color: 'info.main'},
  ACCEPTED: {icon: <CheckCircle fontSize="small" />, color: 'success.main'},
  RESOLVED: {icon: <CheckCircle fontSize="small" />, color: 'success.dark'},
  REJECTED: {icon: <Cancel fontSize="small" />, color: 'error.main'},
};

function formatAudioTime(secs: number): string {
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function DefectCard({
  defect,
  onEdit,
  onDelete,
  onPlayAudio,
}: DefectCardProps) {
  const status = statusConfig[defect.defectStatus] || statusConfig.IDENTIFIED;

  return (
    <Card
      sx={{
        mb: 1,
        borderLeft: 4,
        borderColor:
          defect.severity === 'CRITICAL'
            ? 'error.main'
            : defect.severity === 'HIGH'
            ? 'warning.main'
            : defect.severity === 'MEDIUM'
            ? 'warning.light'
            : 'success.main',
      }}
    >
      <CardContent sx={{py: 1.5, '&:last-child': {pb: 1.5}}}>
        <Stack spacing={1}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Box flex={1}>
              <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {defect.title}
                </Typography>
                {defect.aiDetected && (
                  <Tooltip
                    title={`AI Detected (${Math.round(
                      (defect.aiConfidence || 0) * 100
                    )}% confidence)`}
                  >
                    <SmartToy fontSize="small" sx={{color: 'primary.main'}} />
                  </Tooltip>
                )}
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <SeverityBadge severity={defect.severity} />
                <Chip
                  icon={status.icon as React.ReactElement}
                  label={defect.defectStatus.replace('_', ' ')}
                  size="small"
                  sx={{
                    '& .MuiChip-icon': {color: status.color},
                  }}
                />
              </Stack>
            </Box>
            <Stack direction="row" spacing={0.5}>
              {onPlayAudio && defect.audioStartSeconds != null && (
                <Tooltip
                  title={`Play audio at ${formatAudioTime(
                    defect.audioStartSeconds
                  )}`}
                >
                  <IconButton
                    size="small"
                    onClick={onPlayAudio}
                    color="primary"
                  >
                    <PlayCircleOutline fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {onEdit && (
                <IconButton size="small" onClick={onEdit}>
                  <Edit fontSize="small" />
                </IconButton>
              )}
              {onDelete && (
                <IconButton size="small" onClick={onDelete} color="error">
                  <Delete fontSize="small" />
                </IconButton>
              )}
            </Stack>
          </Box>

          {defect.description && (
            <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>
              {defect.description.length > 150
                ? `${defect.description.substring(0, 150)}...`
                : defect.description}
            </Typography>
          )}

          <Stack direction="row" spacing={1} flexWrap="wrap">
            {defect.roomType && (
              <Chip
                label={defect.roomType}
                size="small"
                variant="outlined"
                sx={{fontSize: '0.7rem'}}
              />
            )}
            {defect.defectCategory && (
              <Chip
                label={defect.defectCategory}
                size="small"
                variant="outlined"
                sx={{fontSize: '0.7rem'}}
              />
            )}
            {defect.sourceClipIndex !== null &&
              defect.sourceClipIndex !== undefined && (
                <Typography variant="caption" color="text.secondary">
                  Clip {defect.sourceClipIndex + 1}
                  {defect.sourceFramePosition &&
                    ` - ${defect.sourceFramePosition}`}
                </Typography>
              )}
          </Stack>

          {defect.evidenceImageURI && (
            <Box
              component="img"
              src={defect.evidenceImageURI}
              alt="Evidence"
              sx={{
                width: '100%',
                maxHeight: 120,
                objectFit: 'cover',
                borderRadius: 1,
                mt: 1,
              }}
            />
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
